/**
 * StatCell — the shared brutalist "stat" primitive used by the landing's KPI
 * surfaces: a big `font-head` value stacked over a small `font-mono` uppercase
 * label. Extracted so the LIGHT KpiBandSection band and the DARK
 * ResearcherDashboardSection grid render the exact same cell shape from one
 * source of truth.
 *
 * The cell is presentation-only and deliberately unopinionated about borders /
 * dividers — the *container* draws the divider rules (so a 2×2 grid, a 4-up row,
 * or a single cell all reuse this). It also doesn't pick a value format: the
 * caller passes the rendered value as `children`, so a cell can show a plain
 * string ("8.4 TB"), a `toLocaleString` number, or a `<CountUp/>` tween without
 * StatCell needing to know which.
 *
 * Preflight is OFF in this tree, so any border in callers must be `border-solid`;
 * StatCell itself emits no borders.
 */
import * as React from 'react';

export interface StatCellProps {
  /** Small uppercase caption under the value (e.g. "Participants"). */
  label: string;
  /**
   * The rendered value. A string, a pre-formatted number, or a `<CountUp/>`.
   * Passing it as children keeps StatCell format-agnostic.
   */
  children: React.ReactNode;
  /** Tailwind text-color class for the value (a distinct brand color per cell). */
  valueClassName?: string;
  /** Tailwind text-color class for the label. Defaults to a muted token. */
  labelClassName?: string;
  /**
   * Value type-scale. `'lg'` is the big light-page band size (text-4xl→5xl);
   * `'md'` is the compact dark-dashboard size (text-2xl→3xl).
   */
  size?: 'lg' | 'md';
  /** Horizontal alignment of the stacked value + label. Defaults to center. */
  align?: 'center' | 'start';
  /** Optional tiny colored delta chip rendered beside the value (e.g. "+12"). */
  delta?: { text: string; className?: string };
  /** Extra classes on the outer cell (padding lives here so callers can tune it). */
  className?: string;
}

const VALUE_SIZE: Record<NonNullable<StatCellProps['size']>, string> = {
  lg: 'text-4xl sm:text-5xl',
  md: 'text-2xl sm:text-3xl',
};

const LABEL_SIZE: Record<NonNullable<StatCellProps['size']>, string> = {
  lg: 'mt-3 text-xs sm:text-sm tracking-[0.18em]',
  md: 'mt-2 text-[10px] sm:text-xs tracking-[0.2em]',
};

/**
 * One stat cell. The container owns dividers + grid placement; this owns the
 * value/label typography only.
 */
const StatCell: React.FC<StatCellProps> = ({
  label,
  children,
  valueClassName = '',
  labelClassName = 'text-muted-foreground',
  size = 'lg',
  align = 'center',
  delta,
  className = 'px-4 py-8 md:py-10',
}) => {
  const alignClass = align === 'center' ? 'items-center text-center' : 'items-start text-left';

  return (
    <div className={['flex flex-col justify-center', alignClass, className].join(' ')}>
      <span className={['flex items-baseline gap-2', align === 'center' ? 'justify-center' : ''].join(' ')}>
        <span
          className={['font-head leading-none tracking-tight', VALUE_SIZE[size], valueClassName].join(
            ' ',
          )}
        >
          {children}
        </span>
        {delta && (
          <span
            className={[
              'font-mono text-[10px] font-bold uppercase tracking-[0.1em]',
              delta.className ?? '',
            ].join(' ')}
          >
            {delta.text}
          </span>
        )}
      </span>
      <span
        className={['font-mono font-bold uppercase', LABEL_SIZE[size], labelClassName].join(' ')}
      >
        {label}
      </span>
    </div>
  );
};

export default StatCell;
